import { Box, Card, CardContent, CardActionArea, Typography, Grid, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useHasRole, useIdentity } from '../config/authConfig';
import { Roles } from '../enum/Roles';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SearchIcon from '@mui/icons-material/Search';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BusinessIcon from '@mui/icons-material/Business';

interface AppCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const AppCard = ({ title, description, icon, onClick }: AppCardProps) => {
  return (
    <Grid
      size={{
        xs: 12,
        sm: 6,
        md: 4
      }}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 3,
          },
        }}
      >
        <CardActionArea
          onClick={onClick}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ mr: 2, color: '#03387E', fontSize: '2rem' }}>{icon}</Box>
            <Typography variant="h5" component="h2" sx={{ color: '#03387E', fontWeight: 'bold' }}>
              {title}
            </Typography>
          </Box>
          <CardContent sx={{ p: 0, flexGrow: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useIdentity();

  // Check for specific roles
  const hasEsaRole = useHasRole([Roles.EsaUser, Roles.EsaAdmin, Roles.Admin]);
  const hasProductFinderRole = useHasRole([Roles.ProductFinderUser, Roles.ProductFinderAdmin, Roles.Admin]);
  const hasTakeOffRole = useHasRole([Roles.TakeOffUser, Roles.TakeOffAdmin, Roles.Admin]);
  const hasDistSyndicationRole = useHasRole([Roles.DistSyndicationUser, Roles.DistSyndicationAdmin, Roles.Admin]);

  const apps = [
    // ESA Chat
    ...(hasEsaRole
      ? [
          {
            title: 'Expert Sales Assistant',
            description:
              'Chat with our AI assistant to get help with product information, technical questions, and sales support.',
            icon: <ChatBubbleOutlineIcon />,
            path: '/chat',
          },
        ]
      : []),

    // Product Finder
    ...(hasProductFinderRole
      ? [
          {
            title: 'Product Finder',
            description: 'Search and discover products using advanced filters and AI-powered recommendations.',
            icon: <SearchIcon />,
            path: '/product-finder',
          },
        ]
      : []),

    // Seton Pipe Markers (Take Off)
    ...(hasTakeOffRole
      ? [
          {
            title: 'Seton Take Off',
            description: 'Upload and process take-off Excel files to automatically add product and catalog numbers.',
            icon: <FileCopyIcon />,
            path: '/seton-pipe-markers',
          },
        ]
      : []),

    // Distributor Syndication
    ...(hasDistSyndicationRole
      ? [
          {
            title: 'Distributor Syndication',
            description: 'Manage and process distributor data syndication workflows and file uploads.',
            icon: <BusinessIcon />,
            path: '/distributor-syndication',
          },
        ]
      : []),

    // Admin Tools
    ...(isAdmin
      ? [
          {
            title: 'User Management',
            description: 'Manage user accounts, roles, and permissions across the platform.',
            icon: <GroupAddIcon />,
            path: '/users',
          },
        ]
      : []),

    // Help - Available to all users
    {
      title: 'Help & Support',
      description: 'Contact our support team for assistance, questions, or technical issues with the platform.',
      icon: <HelpOutlineIcon />,
      path: '/help',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h2"
          component="h1"
          sx={{
            color: '#03387E',
            fontWeight: 'bold',
            mb: 2,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          }}
        >
          Brady Sales Assistant
        </Typography>
        <Typography
          variant="h5"
          color="text.secondary"
          sx={{
            maxWidth: '800px',
            mx: 'auto',
            fontSize: { xs: '1rem', sm: '1.25rem' },
          }}
        >
          Choose an application to get started with your sales and product discovery workflow
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {apps.map((app) => (
          <AppCard
            key={app.path}
            title={app.title}
            description={app.description}
            icon={app.icon}
            onClick={() => navigate(app.path)}
          />
        ))}
      </Grid>

      {apps.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No applications available. Please contact your administrator for access.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default HomePage;
